import Vue from 'vue';

let waitForRefreshPromise = null;

const _addGlobalHeaderKeys = (headers) => {
  const {
    companyId,
    shadowUser,
  } = Vue.prototype.$globals;

  if (companyId) {
    headers['helix-company-id'] = `${companyId}`;
  }

  if (shadowUser) {
    headers['helix-user-id'] = `${shadowUser.id}`;
  }

  const pubSubSessionId = Vue.prototype.$globals.pubSubUtil.sessionId;
  if (pubSubSessionId) {
    headers['helix-pub-sub-session-id'] = pubSubSessionId;
  }

  try {
    headers['helix-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (ex) {}
};

export default class BaseAPIClient {
  constructor($http) {
    this.baseUrl = Vue.prototype.$globalEnv.baseUrl;
    this.http = $http;
  }

  async _refreshToken() {
    if (!waitForRefreshPromise) {
      let succeeded = false;

      // TODO: Remove this once we fully move to HttpOnly cookies
      const refreshToken = Vue.prototype.$globals.refreshToken || '';
      let payload = {};
      if (refreshToken) {
        payload = { refresh: refreshToken };
      }

      waitForRefreshPromise = this.http.post(`${this.baseUrl}/users/token/refresh/`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      }).then((resp) => {
        succeeded = !!(resp && resp.ok);

        // TODO: Remove this once we fully move to HttpOnly cookies
        if (succeeded) {
          Vue.prototype.$globals.accessToken = '';
          Vue.prototype.$globals.refreshToken = '';
          Vue.prototype.$globals.saveCookie();
        }

        return succeeded;
      }).catch((ex) => {
        console.log(ex);
        return false;
      }).finally(() => {
        waitForRefreshPromise = null;
      });
    }
    return waitForRefreshPromise;
  }

  async _get(url, params, options) {
    const companyIdOverride = (options && options.companyIdOverride) || null;
    const affiliateCompanyOverride = (options && options.affiliateCompanyOverride) || null;
    const fromOwner = options && options.fromOwner;
    return this.requestWithParams('get', url, params, { companyIdOverride, affiliateCompanyOverride, fromOwner });
  }

  async _getUrl(url) {
    const headers = {
      'Content-Type': 'application/json',
    };

    _addGlobalHeaderKeys(headers);

    return this.http.get(url, {
      headers,
    }).then(this._handleResponse);
  }

  async _delete(url, params) {
    return this.requestWithParams('delete', url, params);
  }

  async _post(url, data, options) {
    const companyIdOverride = (options && options.companyIdOverride) || null;
    const fromOwner = options && options.fromOwner;
    return this.request('post', url, data, { companyIdOverride, fromOwner });
  }

  async _postMedia(url, formData) {
    return this.request('post', url, formData);
  }

  async _basicAuthPost(url, email, password, params) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Basic ${window.btoa(`${email}:${password}`)}`,
    };

    return this.http.post(`${this.baseUrl}/${url}`, params || {}, {
      headers,
      withCredentials: true,
    }).then(this._handleResponse);
  }

  async _singleUseTokenAuthPost(url, token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `SingleUseToken ${token}`,
    };

    return this.http.post(`${this.baseUrl}/${url}`, {}, {
      headers,
    }).then(this._handleResponse);
  }

  async _put(url, data) {
    return this.request('put', url, data);
  }

  async _patch(url, data, options) {
    const companyIdOverride = (options && options.companyIdOverride) || null;
    const fromOwner = options && options.fromOwner;
    return this.request('patch', url, data, { fromOwner, companyIdOverride });
  }

  _handleResponse(results) {
    if (results && results.ok) {
      return results.body;
    }
    return {};
  }

  // create generic request
  async requestWithParams(action, url, params, options) {
    if (waitForRefreshPromise) {
      await waitForRefreshPromise;
    }
    const affiliateCompanyOverride = (options && options.affiliateCompanyOverride) || null;
    const companyIdOverride = (options && options.companyIdOverride) || null;
    const fromOwner = options && options.fromOwner;

    const headers = {
      'Content-Type': 'application/json',
    };

    _addGlobalHeaderKeys(headers);

    if (companyIdOverride !== null) {
      headers['helix-company-id'] = `${companyIdOverride}`;
    } else if (fromOwner) {
      delete headers['helix-company-id'];
    }

    if (affiliateCompanyOverride && affiliateCompanyOverride.length) {
      headers['filter-affiliate-company-ids'] = affiliateCompanyOverride.join(',');
    }

    let response;
    let firstError;
    let tryToRefresh = false;

    try {
      response = await this.http[action](`${this.baseUrl}/${url}`, {
        headers,
        params: params || {},
        withCredentials: true,
      });
    } catch (ex) {
      firstError = ex;
      tryToRefresh = !tryToRefresh && ex.status === 401;
    }

    if (!response && tryToRefresh) {
      let refreshed = false;
      try {
        refreshed = await this._refreshToken();
      } catch (ex) {
        console.log(ex);
      }

      // only try to refresh once
      if (refreshed) {
        try {
          response = await this.http[action](`${this.baseUrl}/${url}`, {
            headers,
            params: params || {},
            withCredentials: true,
          });
        } catch (ex) { console.log(ex); }
      } else if (Vue.prototype.$globals.routeRequiresAuth || Vue.prototype.$globals.showSiteLoader) {
        // only force logout if app is still loading or route requires auth
        Vue.prototype.$globals.clearUser();
      }
    }

    if (response) {
      return this._handleResponse(response);
    }
    throw firstError;
  }

  async request(action, url, data, options) {
    if (waitForRefreshPromise) {
      await waitForRefreshPromise;
    }

    const companyIdOverride = (options && options.companyIdOverride) || null;
    const fromOwner = options && options.fromOwner;

    const headers = {
      'Content-Type': 'application/json',
    };

    _addGlobalHeaderKeys(headers);

    if (companyIdOverride !== null) {
      headers['helix-company-id'] = `${companyIdOverride}`;
    } else if (fromOwner) {
      delete headers['helix-company-id'];
    }

    let response;
    let firstError;
    let tryToRefresh = false;

    try {
      response = await this.http[action](`${this.baseUrl}/${url}`, data, {
        headers,
        withCredentials: true,
      });
    } catch (ex) {
      firstError = ex;
      tryToRefresh = !tryToRefresh && ex.status === 401;
    }

    if (!response && tryToRefresh) {
      let refreshed = false;
      try {
        refreshed = await this._refreshToken();
      } catch (ex) {
        console.log(ex);
      }

      // only try to refresh once
      if (refreshed) {
        try {
          // re-try the call if we had a refresh success
          response = await this.http[action](`${this.baseUrl}/${url}`, data, {
            headers,
            withCredentials: true,
          });
        } catch (ex) { console.log(ex); }
      } else if (Vue.prototype.$globals.routeRequiresAuth || Vue.prototype.$globals.showSiteLoader) {
        // only force logout if app is still loading or route requires auth
        Vue.prototype.$globals.clearUser();
      }
    }

    if (response) {
      return this._handleResponse(response);
    }
    throw firstError;
  }
}
